import React, { useContext, useState, useEffect } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { getShopifyImage } from 'gatsby-source-shopify';
import styled from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  fontWeights,
  fontSize,
} from '../styles';
import { IncExVatPrice } from './Prices';
import ProductQuantity from './ProductQuantity';
import { StoreContext } from '../context/StoreContext';

const StyledCartItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 18px;

  ${minBreakpointQuery.smedium`
    grid-template-columns: 110px 1fr;
  `}

  ${minBreakpointQuery.large`
    grid-template-columns: 120px 1fr;
  `}
`;

const StyledImage = styled(GatsbyImage)`
  max-height: 100px;
  max-width: 100px;

  ${minBreakpointQuery.smedium`
    max-height: 110px;
    max-width: 110px;
  `}

  ${minBreakpointQuery.large`
    max-height: 120px;
    max-width: 120px;
  `}

  img {
    opacity: 1 !important;
  }
`;

const StyledDetails = styled.div`
  grid-column: 2/3;
`;

const StyledNamePrice = styled.div``;

const StyledName = styled.h4`
  color: ${standardColours.black};
  font-weight: ${fontWeights.semibold};
  ${fontSize(16)};

  ${minBreakpointQuery.large`
    ${fontSize(17)};
  `}
`;

const StyledPrice = styled(IncExVatPrice)`
  margin-top: 6px;
`;

const StyledQuantityRemove = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  color: ${standardColours.darkGrey};

  ${maxBreakpointQuery.tiny`
    flex-direction: column;
    align-items: start;
  `}

  ${minBreakpointQuery.tiny`
    justify-content: space-between;
    align-items: center;
  `}
`;

const StyledRemove = styled.button`
  padding: 0;
  color: ${standardColours.darkGrey};
  ${fontSize(13)};
  background: none;
  border: none;

  span {
    margin-left: 4px;
    text-decoration: underline;
  }
`;

const CartItem = ({
  product: { id, title, quantity: currentQuantity, variant, isRetailProduct },
}) => {
  const { updateProductInCheckout, removeProductFromCheckout } =
    useContext(StoreContext);

  const imageData =
    variant &&
    getShopifyImage({
      image: { originalSrc: variant.image.src },
      layout: 'fixed',
      width: 120,
      height: 120,
    });

  const [quantity, setQuantity] = useState(currentQuantity);

  useEffect(() => {
    setQuantity(currentQuantity);
  }, [currentQuantity]);

  const changeQuantity = async e => {
    const value = e.target.value;
    let changedQuantity;

    if (value === 'remove') {
      changedQuantity = quantity > 0 ? quantity - 1 : 0;
    } else if (value === 'add') {
      changedQuantity = quantity ? quantity + 1 : 1;
    } else {
      changedQuantity = parseInt(value);
    }

    await updateProductInCheckout(id, changedQuantity);
    setQuantity(changedQuantity);
  };

  return (
    <StyledCartItem>
      {variant && (
        <StyledImage image={imageData} alt={variant.image.altText || title} />
      )}
      <StyledDetails>
        <StyledNamePrice>
          <StyledName>
            {title}
            {variant &&
              variant.title !== 'Default Title' &&
              ` (${variant.title})`}
          </StyledName>
          <StyledPrice
            price={variant.priceV2.amount}
            isCartItem={true}
            isRetailProduct={isRetailProduct}
          />
        </StyledNamePrice>
        <StyledQuantityRemove>
          <ProductQuantity
            quantity={quantity}
            quantityDiscounts={variant.quantityDiscounts}
            setQuantity={setQuantity}
            changeFunction={changeQuantity}
            isCartItem={true}
          />
          <StyledRemove onClick={() => removeProductFromCheckout(id)}>
            ✕ <span>Remove</span>
          </StyledRemove>
        </StyledQuantityRemove>
      </StyledDetails>
    </StyledCartItem>
  );
};

export default CartItem;
